
import React from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import type { Application } from '@/types/database.types';

interface EmploymentPracticesProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
}

export const EmploymentPractices = ({
  register,
  watch,
  setValue
}: EmploymentPracticesProps) => {
  const hasRecentLayoffs = watch('has_recent_layoffs') as boolean | undefined;
  const hasPlannedLayoffs = watch('has_planned_layoffs') as boolean | undefined;
  const hasForeignTravel = watch('has_foreign_travel') as boolean | undefined;
  const hasEpliInsurance = watch('has_epli_insurance') as boolean | undefined;
  const hasTerminations = watch('has_terminations') as boolean | undefined;
  const hasOutsourcedOperations = watch('has_outsourced_operations') as boolean | undefined;
  const hasIndependentContractors = watch('has_independent_contractors') as boolean | undefined;
  const hasInternationalEmployees = watch('has_international_employees') as boolean | undefined;
  const hasOcipJobs = watch('has_ocip_jobs') as boolean | undefined;
  const hasUnionizedEmployees = watch('has_unionized_employees') as boolean | undefined;
  const hasGovernmentContracts = watch('has_government_contracts') as boolean | undefined;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Employment Practices</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Please provide information about employment practices and EPLI coverage.
      </p>
      
      <div className="space-y-4">
        {/* EPLI Insurance - moved to top */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_epli_insurance" className="cursor-pointer">
            Does the organization currently have EPLI (Employment Practices Liability Insurance)?
          </Label>
          <Switch
            id="has_epli_insurance"
            checked={Boolean(hasEpliInsurance)}
            onCheckedChange={(checked) => setValue('has_epli_insurance', !!checked)}
          />
        </div>
        
        {hasEpliInsurance && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="epli_insurance_details">EPLI Insurance Details</Label>
            <Textarea
              id="epli_insurance_details"
              {...register('epli_insurance_details')}
              placeholder="Provide details about current EPLI coverage..."
            />
          </div>
        )}

        {/* Terminations - combined field */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_terminations" className="cursor-pointer">
            Has the organization had any voluntary or involuntary terminations in the past 12 months?
          </Label>
          <Switch
            id="has_terminations"
            checked={Boolean(hasTerminations)}
            onCheckedChange={(checked) => setValue('has_terminations', !!checked)}
          />
        </div>
        
        {hasTerminations && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voluntary_terminations">Voluntary Terminations (Past 12 Months)</Label>
                <Input
                  id="voluntary_terminations"
                  type="number"
                  {...register('voluntary_terminations', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="involuntary_terminations">Involuntary Terminations (Past 12 Months)</Label>
                <Input
                  id="involuntary_terminations"
                  type="number"
                  {...register('involuntary_terminations', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        )}

        {/* Recent Layoffs */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_recent_layoffs" className="cursor-pointer">
            Has the organization had recent layoffs (past 12 months)?
          </Label>
          <Switch
            id="has_recent_layoffs"
            checked={Boolean(hasRecentLayoffs)}
            onCheckedChange={(checked) => setValue('has_recent_layoffs', !!checked)}
          />
        </div>
        
        {hasRecentLayoffs && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="space-y-2">
              <Label htmlFor="recent_layoffs_details">Recent Layoffs Details</Label>
              <Textarea
                id="recent_layoffs_details"
                {...register('recent_layoffs_details')}
                placeholder="Provide details about recent layoffs..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="layoff_count">Number of Employees Laid Off</Label>
                <Input
                  id="layoff_count"
                  type="number"
                  {...register('layoff_count', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="layoff_date">Layoff Date</Label>
                <Input
                  id="layoff_date"
                  type="date"
                  {...register('layoff_date')}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="layoff_percentage">Layoff Percentage of Total Workforce</Label>
              <Input
                id="layoff_percentage"
                type="number"
                {...register('layoff_percentage', { valueAsNumber: true })}
                placeholder="0"
                min="0"
                max="100"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="layoff_releases_percentage">Percentage of Laid-Off Employees Who Signed Releases</Label>
              <Input
                id="layoff_releases_percentage"
                type="number"
                {...register('layoff_releases_percentage', { valueAsNumber: true })}
                placeholder="0"
                min="0"
                max="100"
              />
            </div>
          </div>
        )}

        {/* Planned Layoffs */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_planned_layoffs" className="cursor-pointer">
            Does the organization have planned layoffs (next 12 months)?
          </Label>
          <Switch
            id="has_planned_layoffs"
            checked={Boolean(hasPlannedLayoffs)}
            onCheckedChange={(checked) => setValue('has_planned_layoffs', !!checked)}
          />
        </div>
        
        {hasPlannedLayoffs && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="space-y-2">
              <Label htmlFor="planned_layoffs_details">Planned Layoffs Details</Label>
              <Textarea
                id="planned_layoffs_details"
                {...register('planned_layoffs_details')}
                placeholder="Provide details about planned layoffs..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="planned_layoff_count">Number of Employees to Be Laid Off</Label>
                <Input
                  id="planned_layoff_count"
                  type="number"
                  {...register('planned_layoff_count', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="planned_layoff_date">Planned Layoff Date</Label>
                <Input
                  id="planned_layoff_date"
                  type="date"
                  {...register('planned_layoff_date')}
                />
              </div>
            </div>
          </div>
        )}

        {/* Foreign Travel */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_foreign_travel" className="cursor-pointer">
            Do employees travel internationally for business?
          </Label>
          <Switch
            id="has_foreign_travel"
            checked={Boolean(hasForeignTravel)}
            onCheckedChange={(checked) => setValue('has_foreign_travel', !!checked)}
          />
        </div>
        
        {hasForeignTravel && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="space-y-2">
              <Label htmlFor="foreign_travel_details">Foreign Travel Details</Label>
              <Textarea
                id="foreign_travel_details"
                {...register('foreign_travel_details')}
                placeholder="Describe international travel for business purposes..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="foreign_travel_countries">Countries Traveled To</Label>
                <Input
                  id="foreign_travel_countries"
                  {...register('foreign_travel_countries')}
                  placeholder="List countries where employees travel"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="foreign_travel_frequency">Travel Frequency</Label>
                <Input
                  id="foreign_travel_frequency"
                  {...register('foreign_travel_frequency')}
                  placeholder="Monthly, quarterly, annually, etc."
                />
              </div>
            </div>
          </div>
        )}

        {/* Outsourced Operations */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_outsourced_operations" className="cursor-pointer">
            Does the organization outsource any portion of its operations?
          </Label>
          <Switch
            id="has_outsourced_operations"
            checked={Boolean(hasOutsourcedOperations)}
            onCheckedChange={(checked) => setValue('has_outsourced_operations', !!checked)}
          />
        </div>
        
        {hasOutsourcedOperations && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="outsourced_operations_details">Outsourced Operations Details</Label>
            <Textarea
              id="outsourced_operations_details"
              {...register('outsourced_operations_details')}
              placeholder="Describe which operations are outsourced and to whom..."
            />
          </div>
        )}

        {/* Independent Contractors */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_independent_contractors" className="cursor-pointer">
            Does the organization have U.S.-based subcontractors or independent contractors?
          </Label>
          <Switch
            id="has_independent_contractors"
            checked={Boolean(hasIndependentContractors)}
            onCheckedChange={(checked) => setValue('has_independent_contractors', !!checked)}
          />
        </div>
        
        {hasIndependentContractors && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="independent_contractors_details">Independent Contractors Details</Label>
            <Textarea
              id="independent_contractors_details"
              {...register('independent_contractors_details')}
              placeholder="Describe the subcontractors or independent contractors used..."
            />
          </div>
        )}

        {/* International Employees */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_international_employees" className="cursor-pointer">
            Does the organization have, or plan to have any international employees or contractors?
          </Label>
          <Switch
            id="has_international_employees"
            checked={Boolean(hasInternationalEmployees)}
            onCheckedChange={(checked) => setValue('has_international_employees', !!checked)}
          />
        </div>
        
        {hasInternationalEmployees && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="international_employees_details">International Employees Details</Label>
            <Textarea
              id="international_employees_details"
              {...register('international_employees_details')}
              placeholder="Describe international employees or contractors..."
            />
          </div>
        )}

        {/* OCIP Jobs */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_ocip_jobs" className="cursor-pointer">
            Does the organization have any OCIP (Owner Controlled Insurance Program) jobs?
          </Label>
          <Switch
            id="has_ocip_jobs"
            checked={Boolean(hasOcipJobs)}
            onCheckedChange={(checked) => setValue('has_ocip_jobs', !!checked)}
          />
        </div>
        
        {hasOcipJobs && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="ocip_jobs_details">OCIP Jobs Details</Label>
            <Textarea
              id="ocip_jobs_details"
              {...register('ocip_jobs_details')}
              placeholder="Describe OCIP jobs and coverage details..."
            />
          </div>
        )}

        {/* Unionized Employees */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_unionized_employees" className="cursor-pointer">
            Does the organization have unionized employees or has any petition for certification been filed?
          </Label>
          <Switch
            id="has_unionized_employees"
            checked={Boolean(hasUnionizedEmployees)}
            onCheckedChange={(checked) => setValue('has_unionized_employees', !!checked)}
          />
        </div>
        
        {hasUnionizedEmployees && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="unionized_employees_details">Unionized Employees Details</Label>
            <Textarea
              id="unionized_employees_details"
              {...register('unionized_employees_details')}
              placeholder="Describe union membership, certifications, or pending petitions..."
            />
          </div>
        )}

        {/* Government Contracts */}
        <div className="flex items-center justify-between">
          <Label htmlFor="has_government_contracts" className="cursor-pointer">
            Does the organization have or solicit contracts with the Federal Government?
          </Label>
          <Switch
            id="has_government_contracts"
            checked={Boolean(hasGovernmentContracts)}
            onCheckedChange={(checked) => setValue('has_government_contracts', !!checked)}
          />
        </div>
        
        {hasGovernmentContracts && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="government_contracts_details">Government Contracts Details</Label>
            <Textarea
              id="government_contracts_details"
              {...register('government_contracts_details')}
              placeholder="Describe federal government contracts or solicitations..."
            />
          </div>
        )}
      </div>
    </div>
  );
};
